import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import type { FormContext, FormIssue, FormLocale, I18nText, JsonType, PropertySchema, UiField } from "../types";
import { primaryType } from "../schema";

/** Props every widget receives. Widgets are controlled components; the renderer owns the value via react-hook-form. */
export interface WidgetProps<V = unknown> {
  id: string;
  path: string;
  schema: PropertySchema;
  ui: UiField;
  value: V;
  onChange: (value: V) => void;
  onBlur: () => void;
  required: boolean;
  disabled: boolean;
  readOnly: boolean;
  invalid: boolean;
  locale: FormLocale;
  context: FormContext;
  /** Merged `x-ncp.options` + `ui.options`. */
  options: Record<string, unknown>;
  /** Localised label/placeholder/hint resolved by the renderer. */
  label: string;
  placeholder?: string;
  /** Nested rendering for composite widgets (array / object groups). */
  renderChild?: (path: string, schema: PropertySchema, ui?: Partial<UiField>) => React.ReactNode;
  /** Error message for this field, when any. */
  error?: string;
}

export type WidgetCategory = "basic" | "choice" | "number" | "date" | "composite" | "documents" | "layout";

export interface WidgetDefinition {
  key: string;
  label: I18nText;
  description?: I18nText;
  icon?: LucideIcon;
  category: WidgetCategory;
  /** Schema types this widget can edit. */
  forTypes: JsonType[];
  component: ComponentType<WidgetProps>;
  /** Read-only renderer; falls back to a generic value formatter. */
  readOnly?: ComponentType<WidgetProps>;
  /** Schema created when the widget is dropped from the builder palette. */
  defaultSchema: () => PropertySchema;
  /** Widget renders its own label (tables, checklists, checkboxes). */
  ownLabel?: boolean;
  /** Default grid span when placed (1–12). */
  defaultSpan?: number;
  /** Extra validation beyond JSON Schema (e.g. required documents present). */
  validate?: (value: unknown, options: Record<string, unknown>, locale: FormLocale, path: string) => FormIssue[];
  /** Options editable in the builder's properties panel. */
  optionFields?: Array<{ key: string; label: I18nText; type: "text" | "number" | "boolean" | "select" | "list"; choices?: string[]; hint?: I18nText }>;
}

const registry = new Map<string, WidgetDefinition>();

export function registerWidget(def: WidgetDefinition): void {
  registry.set(def.key, def);
}

export function getWidget(key: string | undefined): WidgetDefinition | undefined {
  return key ? registry.get(key) : undefined;
}

export function listWidgets(): WidgetDefinition[] {
  return [...registry.values()];
}

/** Merged widget options: `x-ncp.options` overridden by the placement's `options`. */
export function widgetOptions(schema: PropertySchema, ui?: UiField): Record<string, unknown> {
  return { ...(schema["x-ncp"]?.options ?? {}), ...(ui?.options ?? {}) };
}

/** Picks the widget for a schema node: explicit placement override → `x-ncp.widget` → inferred from type/format/enum. */
export function resolveWidgetKey(schema: PropertySchema, ui?: UiField): string {
  if (ui?.widget && registry.has(ui.widget)) return ui.widget;
  const x = schema["x-ncp"];
  if (x?.widget && registry.has(x.widget)) return x.widget;
  const type = primaryType(schema);
  if (schema.enum || x?.enumOptions) return (schema.enum?.length ?? x?.enumOptions?.length ?? 0) <= 4 ? "radio" : "select";
  switch (type) {
    case "boolean":
      return "checkbox";
    case "integer":
    case "number":
      return schema.format === "currency" ? "currency" : schema.format === "percent" ? "percent" : "number";
    case "array":
      if (schema.items?.enum) return "multiselect";
      if (schema.items?.["x-ncp"]?.widget === "party") return "partyList";
      if (schema.items?.["x-ncp"]?.widget === "file") return "fileList";
      return "array";
    case "object":
      return "object";
    default:
      switch (schema.format) {
        case "date":
          return "date";
        case "date-time":
          return "datetime";
        case "email":
          return "email";
        case "phone":
          return "phone";
        case "uri":
          return "url";
        default:
          return (schema.maxLength ?? 0) > 200 || x?.widget === "textarea" ? "textarea" : "text";
      }
  }
}
